import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Button } from "@mui/material";
import DynamicForm from "../../components/DynamicForm";
import DocumentEditor from "../../components/documentEditor/documentEditor";
import { makeStyles } from "@material-ui/core/styles";
import Loading from "../../../../components/loading";
import {
  fillDocForm,
  fillDocFormBySender,
  updateSignature,
} from "../../../../services/templateV2Service";
import { useSignatureForms } from "../../hooks/useSignatureForms";
import { useDocFormFiller } from "../../hooks/useDocFormFiller";
import { useFormCollaboration } from "../../hooks/useFormCollaboration";
import { getSignerDisplayNameFromStorage } from "../../utils/initials";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0),
    height: "calc(100vh - 148px)",
    backgroundColor: "#EFF3F7",
  },
  headerContainer: {
    color: "#0076FF",
    padding: theme.spacing(1),
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0),
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "32px",
    color: "#0076FF",
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontWeight: 400,
    fontSize: "16px",
    color: "#0076FF",
  },
  container: {
    height: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "21px",
  },
  pdfContainer: {
    height: "100%",
    overflow: "hidden",
    width: "65%",
    borderRadius: "1px",
  },
  formContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    padding: "24px 32px",
    backgroundColor: "#fff",
    width: "35%",
    borderRadius: "10px",
    boxShadow: theme.shadows[2],
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    backgroundColor: "white",
    marginTop: "24px",
    marginBottom: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #E0E0E0",
    gap: "16px",
  },
  rejectButton: {
    borderColor: "#0076FF",
    color: "#0076FF",
    width: "100%",
    padding: "8px 16px",
    borderRadius: "10px",
  },
  completeButton: {
    backgroundColor: "#0076FF",
    color: "white",
    "&:hover": {
      backgroundColor: "#0076FF",
    },
    width: "180px",
    padding: "8px 16px",
    borderRadius: "10px",
  },
  formTitle: {
    fontWeight: 700,
    fontSize: "32px",
    color: "#000",
    marginBottom: theme.spacing(1),
  },
  formSubtitle: {
    fontWeight: 400,
    fontSize: "16px",
    color: "#000",
    marginBottom: theme.spacing(0.5),
  },
  formMeta: {
    fontWeight: 400,
    fontSize: "13px",
    color: "#555",
    marginBottom: theme.spacing(1.5),
  },
  divider: {
    width: "100%",
    height: "1px",
    backgroundColor: "#0076FF",
    marginBottom: theme.spacing(1),
  },
  formContent: {
    flex: 1,
  },
}));

function Header() {
  const classes = useStyles();
  return (
    <div className={classes.headerContainer}>
      <h2 className={classes.headerTitle}>Review and Complete</h2>
    </div>
  );
}

function DocFormFiller() {
  const { id, idRequest } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const {
    docFormFiller,
    getDocFormFillerData,
    fields,
    urlPdf,
    values,
    setValue,
    getDocFormFillerBySenderData,
    setUrlPdf,
    flowId,
  } = useDocFormFiller();
  const { signatureImage, getSignatureList } = useSignatureForms();
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const scrollToFieldRef = useRef<((fieldId: string) => void) | null>(null);

  const handleFieldChanged = useCallback((fieldId: string, fieldValue: string, userId: string) => {
    setValue(fieldId, fieldValue);
  }, [setValue]);

  const { sendFieldUpdate } = useFormCollaboration(idRequest ?? "", handleFieldChanged, flowId);

  useEffect(() => {
    fetchData();
  }, [id, searchParams]);

  const fetchData = async () => {
    if (id) {
      const fillBy = searchParams.get("fillBy");
      const urlPdf = searchParams.get("urlPdf");
      console.log("urlPdf-------------------", urlPdf);
      if (fillBy === "sender") {
        await getDocFormFillerBySenderData(id);
        if (urlPdf) {
          setUrlPdf(urlPdf);
        }
      } else {
        getDocFormFillerData(id, idRequest);
      }
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setLoadingMessage("Filling form...");
    const fillBy = searchParams.get("fillBy");
    try {
      /*
			 values:[
				{
					fieldId: "1",
					fieldValue: "value1",
					fieldType: "text",
				}
			 ]
			 */

      if (fillBy === "sender") {
        const valuesToSend = values.map(
          (value: { id: string; value: string; fieldType: string }) => ({
            fieldId: value.id,
            fieldValue: value.value,
            fieldType: value.fieldType,
            fieldAssignee: "sender",
          })
        );
        console.log("fillDocFormBySender", valuesToSend);
        await fillDocFormBySender(idRequest, valuesToSend);
      } else {
        const valuesToSend = values.map(
          (value: { id: string; value: string; fieldType: string }) => ({
            fieldId: value.id,
            fieldValue: value.value,
            fieldType: value.fieldType,
          })
        );
        console.log("fillDocForm", valuesToSend);
        await fillDocForm(idRequest, valuesToSend);
      }
      setLoadingMessage("Form filled successfully");
      navigate("/templates-v2");
      toast.success("Form filled successfully");
    } catch (error) {
      console.error("Error filling doc form:", error);
      toast.error("Error filling form");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (
    fieldId: string,
    value: string,
    fieldType?: string
  ) => {
    setValue(fieldId, value);
  };

  const handleFieldBlur = useCallback((
    fieldId: string,
    value: string,
    fieldType?: string
  ) => {
    sendFieldUpdate(fieldId, value);
  }, [sendFieldUpdate]);

  const handleFieldFocus = useCallback((fieldId: string) => {
    setActiveFieldId(fieldId);
    if (scrollToFieldRef.current) {
      scrollToFieldRef.current(fieldId);
    }
  }, []);

  const handleSaveSignature = async (signatureData: string) => {
    // Aquí puedes implementar la lógica para guardar la firma en el perfil del usuario
    // Por ejemplo, llamar a un servicio API
    console.log("Saving signature to profile:", signatureData);
    toast.success("Signature saved to profile successfully");
    try {
      const response = await updateSignature(signatureData);
      console.log("response", response);
      getSignatureList();
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Error saving signature");
    }
  };

  const docFormFillerData = (docFormFiller || {}) as any;

  const documentName =
    docFormFillerData.doc?.name ??
    docFormFillerData.name ??
    "Document";

  const typedFields: any[] = (fields || []) as any[];

  const totalRequiredFields = Array.isArray(typedFields)
    ? typedFields.filter((field) => field.required || field.isRequired).length
    : 0;

  const completedRequiredFields =
    totalRequiredFields > 0 && Array.isArray(values)
      ? values.filter((value: any) => {
          const field = typedFields.find(
            (f: any) => f.fieldId === value.id || f.id === value.id
          );
          const anyField = field as any;
          const isRequired = anyField?.required || anyField?.isRequired;
          return (
            isRequired &&
            value.value &&
            value.value.toString().trim() !== ""
          );
        }).length
      : 0;

  const remainingRequiredFields =
    totalRequiredFields > completedRequiredFields
      ? totalRequiredFields - completedRequiredFields
      : 0;

  return (
    <div className={classes.root}>
      <ToastContainer autoClose={3000} />
      <Loading show={loading} text={loadingMessage} />

      <Header />
      <div className={classes.container}>
        <div className={classes.pdfContainer}>
          {urlPdf && fields.length > 0 && (
            <DocumentEditor
              fileUrl={urlPdf}
              fields={fields}
              onFieldChange={handleFieldChange}
              fieldValues={values}
              activeFieldId={activeFieldId}
              scrollToFieldRef={scrollToFieldRef}
            />
          )}
        </div>
        <div className={classes.formContainer}>
          <div>
            <h2 className={classes.formTitle}>Form</h2>
            <p className={classes.formSubtitle}>
              Complete the required fields to finalize this document.
            </p>
            {docFormFiller && documentName !== "Document" && (
              <p className={classes.formMeta}>
                <strong>Document</strong> {documentName}
              </p>
            )}
            {totalRequiredFields > 0 && (
              <p className={classes.formMeta}>
                {remainingRequiredFields > 0
                  ? `${remainingRequiredFields} field${
                      remainingRequiredFields > 1 ? "s" : ""
                    } remaining`
                  : "✓ Ready to complete"}
              </p>
            )}
          </div>
          <div className={classes.divider}></div>
          <div className={classes.formContent}>
            <DynamicForm
              values={values}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              onFocus={handleFieldFocus}
              activeFieldId={activeFieldId}
              signatureImage={signatureImage}
              onSaveSignature={handleSaveSignature}
              signerDisplayName={getSignerDisplayNameFromStorage()}
            />
          </div>
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              className={classes.completeButton}
              onClick={handleComplete}
              size="large"
              color="primary"
            >
              Complete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocFormFiller;
